import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/checkout/status/${sessionId}`,
        { headers: getAuthHeader() }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      }

      if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#005F73] animate-spin mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-semibold text-[#005F73] mb-2">
            Vérification du paiement...
          </h2>
          <p className="text-stone-600">Veuillez patienter quelques instants</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-2xl font-semibold text-[#005F73] mb-2">
            Erreur de paiement
          </h2>
          <p className="text-stone-600 mb-6">
            Nous n'avons pas pu confirmer votre paiement. Veuillez réessayer ou contacter le support.
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            className="rounded-full px-8 py-6 bg-[#005F73] hover:bg-[#005F73]/90"
          >
            Retour aux plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-sand flex items-center justify-center">
      <div className="text-center max-w-md">
        <CheckCircle className="w-20 h-20 text-[#0A9396] mx-auto mb-6" strokeWidth={1.5} />
        <h1 className="text-4xl font-semibold text-[#005F73] mb-4">
          Paiement réussi !
        </h1>
        <p className="text-lg text-stone-600 mb-2">
          Votre abonnement a été activé avec succès. Vous pouvez maintenant profiter de toutes les fonctionnalités de votre plan.
        </p>
        <p className="text-sm text-stone-400 mb-8">
          Votre abonnement sera renouvelé automatiquement chaque mois. Vous pouvez l'annuler à tout moment depuis votre page "Mon compte".
        </p>
        <div className="flex gap-4 justify-center">
          <Button
            onClick={() => navigate('/calculator')}
            className="rounded-full px-8 py-6 bg-[#EE9B00] hover:bg-[#EE9B00]/90"
          >
            Créer un projet
          </Button>
          <Button
            onClick={() => navigate('/projects')}
            variant="outline"
            className="rounded-full px-8 py-6 border-2 border-[#005F73] text-[#005F73] hover:bg-[#005F73]/5"
          >
            Mes projets
          </Button>
        </div>
      </div>
    </div>
  );
}
